'use client'

import React from 'react'

import type { HolidayTableSection } from '@/src/types/holidays'
import { getCarrierCode } from '@/src/utils/holidays/getCarrierCode'

import HolidayTable, { type HolidayTableColumn } from './holidayTable'

const EMPTY_CELL = '—'

interface HolidayTableWrapperProps {
    section: HolidayTableSection
    /** Draw the carrier's logo beside any cell that is a flight number. Flight tables only. */
    withAirlineLogos?: boolean
    isMobile: boolean
}

const HolidayTableWrapper: React.FC<HolidayTableWrapperProps> = ({ section, withAirlineLogos = false, isMobile }) => {
    if (!section.columns?.length || !section.rows?.length) return null

    const columns: HolidayTableColumn[] = section.columns.map(column => ({
        key: column.key,
        label: column.label,
        align: column.align ?? 'left'
    }))

    // Rows are keyed off the columns here so a JSON row missing a key renders an em dash rather
    // than shifting every later cell one column to the left.
    const rows = section.rows.map((row, index) => ({
        id: `${section.id}-row-${index}`,
        cells: columns.map(column => {
            const value = row[column.key] ?? EMPTY_CELL
            // Detected from the cell, not from the column label: a date, a time, an airport code
            // and "TBA" are all rejected by the pattern, so only real flight numbers get a logo.
            return {
                key: column.key,
                value,
                align: column.align,
                carrierCode: withAirlineLogos ? getCarrierCode(value) : undefined
            }
        })
    }))

    /*
     * Cells wrap rather than scroll. The one exception is a dense table on a phone: six pricing
     * columns across 375px would leave ~50px each, so those keep a floor and scroll instead.
     */
    const minWidth = isMobile && columns.length > 3 ? 520 : undefined

    return (
        <HolidayTable minWidth={minWidth} columns={columns} rows={rows} caption={section.caption} isMobile={isMobile} />
    )
}

export { HolidayTableWrapper }
export default HolidayTableWrapper
